// src/components/StudentTable.js

import React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, TextField, Chip, Tooltip, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoIcon from '@mui/icons-material/Info';

const getStatusStyle = (status) => {
  switch (status) {
    case 'Paid':
      return {
        padding: '6px 12px',
        color: '#1a7f37',
        backgroundColor: '#e6f4ea',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        border: '1px solid #1a7f37',
      };
    case 'Unpaid':
      return {
        padding: '6px 12px',
        color: '#d14343',
        backgroundColor: '#fde7e9',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        border: '1px solid #d14343',
      };
    default:
      return {
        padding: '6px 12px',
        color: '#856404',
        backgroundColor: '#fff3cd',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontWeight: 600,
        border: '1px solid #856404',
      };
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Paid':
      return <CheckCircleIcon style={{ fontSize: '16px' }} />;
    case 'Unpaid':
      return <CancelIcon style={{ fontSize: '16px' }} />;
    default:
      return <PendingIcon style={{ fontSize: '16px' }} />;
  }
};

// Helper function to get multi-date payment status
const getMultiDatePaymentStatus = (student, dates, getPaymentStatusForDate) => {
  if (!dates || dates.length === 0) return { paidCount: 0, totalDates: 0, status: 'No dates selected' };

  const statuses = dates.map(date => getPaymentStatusForDate(student, date));
  const paidCount = statuses.filter(s => s.status === 'Paid').length;
  const totalDates = dates.length;

  if (paidCount === totalDates) return { paidCount, totalDates, status: 'All Paid' };
  if (paidCount === 0) return { paidCount, totalDates, status: 'Unpaid' };
  return { paidCount, totalDates, status: 'Partial' };
};

const StudentTable = ({ 
  students, 
  selected, 
  handleSelect,
  handleAmountChange,
  handleSelectAll,
  handlePayment,
  selectedDate,
  getPaymentStatusForDate,
  canteenStatus,
  studentAmounts = {},
  multiDateMode = false,
  selectedDates = []
}) => {
  return (
    <TableContainer component={Paper}>
      <div style={{ 
        padding: '15px 20px', 
        backgroundColor: multiDateMode ? '#e8f5e8' : '#e3f2fd',
        borderBottom: '1px solid #bbdefb',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontWeight: 'bold', color: multiDateMode ? '#2e7d32' : '#1976d2' }}>
          {multiDateMode ? '📅📅' : '📅'}
        </span>
        <span style={{ color: multiDateMode ? '#2e7d32' : '#1976d2', fontWeight: '500' }}>
          {multiDateMode ?
            `Multi-day payment mode: ${selectedDates.length} dates selected` :
            `Showing canteen fees status for: ${new Date(selectedDate).toLocaleDateString('en-US', {
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
            })}`
          }
        </span>
        {canteenStatus && !multiDateMode && (
          <span style={{ fontSize: '12px', color: '#666', marginLeft: 'auto' }}>
            Data from backend • Last sync: {new Date().toLocaleTimeString()}
          </span>
        )}
      </div>
      
      <Table sx={{ minWidth: 650 }} aria-label="canteen fees table">
        <TableHead>
          <TableRow style={{ backgroundColor: '#f5f5f5' }}>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selected.length > 0 && selected.length < students.length}
                checked={students.length > 0 && selected.length === students.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Student</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Class</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Payment Status</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Amount</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Payment Details</TableCell>
            <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {students.map((student) => {
            const paymentStatus = multiDateMode ?
              getMultiDatePaymentStatus(student, selectedDates, getPaymentStatusForDate) :
              getPaymentStatusForDate(student, selectedDate);
            const isSelected = selected.includes(student._id);
            const isPaid = multiDateMode ?
              paymentStatus.status === 'All Paid' :
              paymentStatus.status === 'Paid';
            
            return (
              <TableRow
                key={student._id}
                style={{ 
                  backgroundColor: isPaid ? '#f8fff8' : '#fff8f8',
                  opacity: isPaid ? 0.8 : 1
                }}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleSelect(student._id)}
                    color={isPaid ? "success" : "primary"}
                  />
                  {isPaid && (
                    <div style={{ fontSize: '10px', color: '#1a7f37', textAlign: 'center' }}>
                      Paid
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img
                      src={`/uploads/${student.image}`}
                      alt={student.lastname}
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #e0e0e0'
                      }}
                      onError={(e) => {
                        e.target.src = '/images/default-avatar.png';
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', color: '#333' }}>
                        {student.lastname} {student.othernames}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        ID: {student.ID}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={student.class_} 
                    size="small" 
                    style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                  />
                </TableCell>
                <TableCell>
                  {multiDateMode ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: paymentStatus.status === 'All Paid' ? '#e6f4ea' :
                                       paymentStatus.status === 'Partial' ? '#fff3cd' : '#fde7e9',
                        color: paymentStatus.status === 'All Paid' ? '#1a7f37' :
                              paymentStatus.status === 'Partial' ? '#856404' : '#d14343',
                        border: `1px solid ${paymentStatus.status === 'All Paid' ? '#1a7f37' :
                                           paymentStatus.status === 'Partial' ? '#856404' : '#d14343'}`
                      }}>
                        {paymentStatus.status}
                      </div>
                      <div style={{ fontSize: '10px', color: '#666', textAlign: 'center' }}>
                        {paymentStatus.paidCount}/{paymentStatus.totalDates} days
                      </div>
                    </div>
                  ) : (
                  <div style={getStatusStyle(paymentStatus.status)}>
                    {getStatusIcon(paymentStatus.status)}
                    {paymentStatus.status}
                  </div>
                  )}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {isPaid && (
                      <div style={{ color: '#1a7f37', fontWeight: '600', fontSize: '12px' }}>
                        Paid: GHC {parseFloat(paymentStatus.amount).toFixed(2)}
                      </div>
                    )}
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Enter amount"
                      value={studentAmounts[student._id] || ''}
                      onChange={(e) => handleAmountChange(student._id, e.target.value)}
                      inputProps={{ min: 0, step: 0.01 }}
                      style={{ width: '120px' }}
                      label={isPaid ? "Update Amount" : "Amount"}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  {isPaid ? (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                        <ReceiptIcon style={{ fontSize: '14px' }} />
                        <span style={{ fontWeight: '500' }}>
                          {paymentStatus.receiptNumber || 'No Receipt'}
                        </span>
                      </div>
                      <div>
                        Paid: {paymentStatus.paidDate ? 
                          new Date(paymentStatus.paidDate).toLocaleDateString() : 
                          'Unknown date'
                        }
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
                      No payment recorded
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {isPaid ? (
                    <Tooltip title="Payment completed">
                      <IconButton size="small" disabled>
                        <CheckCircleIcon style={{ color: '#1a7f37' }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Mark as paid">
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => {
                          handleSelect(student._id);
                          if (student.amount) {
                            handlePayment();
                          }
                        }}
                        disabled={!student.amount}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
      {students.length === 0 && (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#666',
          backgroundColor: '#fafafa'
        }}>
          <div style={{ fontSize: '18px', marginBottom: '10px' }}>
            No students found for the selected criteria
          </div>
          <div style={{ fontSize: '14px' }}>
            Try adjusting your search, class filter, or date selection
          </div>
        </div>
      )}
    </TableContainer>
  );
};

export default StudentTable;
